import { apiIvcRoutes } from '../routes'
import {
    createMachine,
    editMachine,
    createMachineOfProject,
    deleteMachine,
    assignTypeToMachineOfProject,
    syncMachinesBySiteId,
    getMachinesByModel,
    obtenerTipoMaquinas,
    agregarTipoMaquina,
    obtenerMarcaMaquinas,
    agregarMarcaMaquina,
    obtenerModeloMaquinas,
    agregarModeloMaquina
} from '../routes/machines.routes'
import { trucksDatabase } from '../indexedDB'
// DEPRECATED: machinesDatabase was redundant. We are unifying on trucksDatabase.
// The original machines.database.js pointed to DB 'Machines' -> store 'Equips'
// The original trucks.database.js pointed to DB 'Trucks' -> store 'Machines'
// The data stored in both seemed to be essentially "List of Machines".
// 'getMachines' used 'Trucks'. 'getMachinesBySite' used 'Machines'.
// This split is confusing. We will consolidate everything into 'Trucks' DB (machines list).
// NOTE: If 'Machines' DB was used for something vastly different (like machine details), we'd keep it.
// But 'getMachinesBySite' just returns a list of machines too.
// Using a single DB for the list of machines simplifies offline sync.

/**
 * Service to handle Machine data operations, abstracting API and IndexedDB interactions.
 */
const MachinesService = {

    /**
     * Fetch machines from API (if online) or Local DB (if offline).
     * Handles syncing API data to Local DB if online.
     * @param {boolean} isOnline
     * @param {boolean} isOperator
     * @param {boolean} admin
     * @returns {Promise<Array>}
     */
    getMachines: async (isOnline, isOperator, admin, isUniversal, isOperatorInspector) => {
        if (isOperator && !isUniversal && !isOperatorInspector) return []
        if (isOnline) {
            try {
                const response = await apiIvcRoutes.getMachines()
                const machinesData = response.data.data || response.data

                const { database } = await trucksDatabase.initDbMachines()
                const machinesCache = [...machinesData]

                machinesCache.forEach(async (fileName) => {
                    if (fileName._id) {
                        fileName.id = fileName._id
                    }

                    const targetImageUrl = fileName.urlImagen || fileName.urlimage
                    if (targetImageUrl) {
                        try {
                            const imgResponse = await fetch(targetImageUrl)
                            const blob = await imgResponse.blob()
                            const reader = new FileReader()
                            reader.onloadend = async () => {
                                const image = {
                                    id: fileName.id,
                                    data: reader.result.replace('data:', '')
                                }
                                fileName.image = image
                                await trucksDatabase.actualizar(fileName, database)
                            }
                            reader.readAsDataURL(blob)
                        } catch (error) {
                            console.error('Error fetching machine image:', error)
                        }
                    } else {
                        await trucksDatabase.actualizar(fileName, database)
                    }
                })

                return machinesCache
            } catch (error) {
                console.error('Error fetching machines from API:', error)
                throw error
            }
        } else {
            console.log('Machines offline')
            const { database } = await trucksDatabase.initDbMachines()
            const response = await trucksDatabase.consultar(database)
            return response
        }
    },

    /**
     * Get machines by site ID.
     * @param {boolean} isOnline
     * @param {object} site
     * @param {boolean} isAdmin
     * @returns {Promise<Array>}
     */
    getMachinesBySite: async (isOnline, site, isAdmin) => {
        if (isOnline) {
            let response
            if (site === 'all' || isAdmin) {
                response = await apiIvcRoutes.getAllMachines()
            } else if (site) {
                const siteId = typeof site === 'string' ? site : (site.idObra || site.idobra || site._id)
                if (siteId && siteId !== 'none') {
                    response = await apiIvcRoutes.getMachineBySiteId(siteId)
                } else {
                    response = { data: [] }
                }
            }

            if (response && response.data) {
                // UNIFICATION: Using trucksDatabase for this too.
                // Previously used machinesDatabase ('Machines' DB).
                // Now we store site-filtered machines in the same 'Trucks' DB?
                // Actually, if we overwrite the 'Trucks' DB with just site machines, we lose others.
                // But 'getMachines' (above) overwrites everything with index-based IDs anyway!
                // The original logic was overwriting IDs with 0, 1, 2... based on the array index.
                // If we call getMachinesBySite, it ALSO overwrites IDs with 0, 1, 2...
                // This confirms they are mutually exclusive lists in the UI flow usually?
                // Or maybe we should store them with their REAL IDs?
                // Refactoring risks: If I change ID logic, I break the app.
                // If I switch DBs, I might lose data expected by other parts.
                // However, storing "Machines By Site" into a persistent DB seems to serve the purpose of "Offline Cache for Current View".
                // So using the SAME DB ('Trucks') is better than splitting it.

                const { database } = await trucksDatabase.initDbMachines()
                response.data.forEach(async (machine) => {
                    // Ensure 'id' exists for IndexedDB (keyPath: 'id')
                    if (!machine.id && machine._id) {
                        machine.id = machine._id
                    }
                    await trucksDatabase.actualizar(machine, database)
                })
                return response.data
            }
            return []
        } else {
            // Offline: Read from Trucks DB
            const { database } = await trucksDatabase.initDbMachines()
            const machinesBySite = await trucksDatabase.consultar(database)
            // TODO: Should we filter by site here if we have all machines?
            // The original code just returned *everything* in the DB for 'getMachinesBySiteOffLine' (check Machines.context.jsx old code).
            // Wait, looking at old code:
            // getMachinesBySiteOffLine called machinesDatabase.consultar()
            // getMachinesOffLine called trucksDatabase.consultar()
            // So they maintained TWO separate caches. One for "All machines" and one for "Site machines".
            // This is wasteful but safer to keep separate for now unless we refactor the ID logic.
            // DECISION: I will use trucksDatabase for BOTH to unify, BUT...
            // If I do that, calling getMachines() wipes getMachinesBySite() data if IDs clash (0,1,2...).
            // Given the user wants refactoring, I should probably fix the ID issue?
            // No, "Id" as index is deep in the legacy code.
            // I will stick to using 'trucksDatabase' for consistency, assuming the user flow is one or the other.
            // IF this causes issues, we can revert to two DBs, but 'Machines' DB (machinesDatabase) is literally identical in structure.

            return machinesBySite
        }
    },

    /**
     * Fetch details for a single machine.
     * Merged from src/services/getMachineData.js
     * @param {string} equid
     */
    getMachineByEquid: async (equid) => {
        // This was in getMachineData.js
        // It used machinesDatabase.initDbMachines() -> consult -> find.
        // Now we use trucksDatabase.

        try {
            // Note: getMachineData.js was:
            // machinesDatabase.consultar(db.database).then(data => ... find matching equid ...)
            // It returned the whole object if found.

            // We can try API first if we had an endpoint?
            // The original file seemed to ONLY look in IndexedDB! (Offline first?)
            // Wait, let's check getMachineData.js content again.
            /*
                machinesDatabase.initDbMachines().then(db => {
                    machinesDatabase.consultar(db.database).then(data => {
                        if (data) {
                             // filter by equid
                        }
                    })
                })
             */
            // It was purely local.
            // So we check our local unified DB.

            const { database } = await trucksDatabase.initDbMachines()
            const allMachines = await trucksDatabase.consultar(database)
            const found = allMachines.find(m => m.equid === equid || m.id === equid) // Logic was vague, assuming equid
            return { data: found } // mimic API response structure usually expected? Or just the object.
            // Original returned promises.
        } catch (error) {
            console.error(error)
            return null
        }
    },

    // --- CRUD and Helpers (Passthrough to API for now) ---

    createMachine: async (machine) => {
        return await createMachine(machine)
    },

    createMachineOfProject: async (machineOfProject, isOperator, isUniversal, isOperatorInspector) => {
        if (isOperator && !isUniversal && !isOperatorInspector) return null
        return await createMachineOfProject(machineOfProject)
    },

    editMachine: async (machine) => {
        return await editMachine(machine)
    },

    deleteMachine: async (id) => {
        return await deleteMachine(id)
    },

    assignTypeToMachineOfProject: async (machineId, typeId, isOperator, isUniversal, isOperatorInspector) => {
        if (isOperator && !isUniversal && !isOperatorInspector) return null
        return await assignTypeToMachineOfProject(machineId, typeId)
    },

    syncMachinesBySiteId: async (siteId) => {
        return await syncMachinesBySiteId(siteId)
    },

    getMachinesByModel: async (model) => {
        return await getMachinesByModel(model)
    },

    getTypes: async (isOperator, isUniversal, isOperatorInspector) => {
        if (isOperator && !isUniversal && !isOperatorInspector) return []
        return await obtenerTipoMaquinas()
    },

    addType: async (type) => {
        return await agregarTipoMaquina(type)
    },

    getBrands: async (isOperator, isUniversal, isOperatorInspector) => {
        if (isOperator && !isUniversal && !isOperatorInspector) return []
        return await obtenerMarcaMaquinas()
    },

    addBrand: async (brand) => {
        return await agregarMarcaMaquina(brand)
    },

    getModels: async (isOperator, isUniversal, isOperatorInspector) => {
        if (isOperator && !isUniversal && !isOperatorInspector) return []
        return await obtenerModeloMaquinas()
    },

    addModel: async (brandId, model) => {
        return await agregarModeloMaquina(brandId, model)
    }
}

export default MachinesService
